// src/pages/Donation/StepPages/PaymentDetails/index.jsx
import React, { useState, useEffect, useRef } from "react";
import "./index.scss";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import CustomLayout from "../../../../components/CustomLayout";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomSpinner from "../../../../components/CustomSpinner";
import TipModal from "../../../../components/TipModal";
import { CardElement, useStripe, useElements, PaymentRequestButtonElement } from "@stripe/react-stripe-js";
import {
  MdLock,
  MdPerson,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdDoorBack,
  MdLocationCity,
  MdMarkunreadMailbox,
  MdMap,
  MdPublic,
  MdAddBusiness
} from "react-icons/md";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { useLoadScript } from "@react-google-maps/api";

import { createSetupIntent } from "../../../../api/payments";
import {
  verifyEmail,
  verifyPhone,
  getIpAddress
} from "../../../../api/verification";
import {
  parseBreakdown,
  calculateTotalAmount,
  calculateProcessingFee
} from "../../../../utils/helpers";

import { motion } from "framer-motion";
import {
  optionsVariants,
  detailsFormVariants
} from "../../../../assets/framer/styles";

// -- i18n
import { useTranslation } from "react-i18next";

// Stable IDs for payment methods (instead of storing translated text in state)
const PAYMENT_METHODS = {
  CREDIT_CARD: "creditCard",
  GOOGLE_APPLE_PAY: "googleApplePay",
  ONLINE_BANKING: "acss_debit"
};

export default function PaymentDetails() {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const [searchParams, setSearchParams] = useSearchParams();

  // Store stable IDs in state, not translated strings
  const [selectedOption, setSelectedOption] = useState(
    PAYMENT_METHODS.CREDIT_CARD
  );

  const [isProcessingFeeChecked, setIsProcessingFeeChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [checkingPaymentAvailability, setCheckingPaymentAvailability] = useState(false);
  const paymentRequestButtonRef = useRef(null);

  const initialFormValues = {
    firstName: "",
    middleName: "",
    businessName: "",
    lastName: "",
    email: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    unitNumber: "",
    city: "",
    postalCode: "",
    selectedRegion: "",
    selectedCountry: "CA"
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState({});

  const breakdown = parseBreakdown(searchParams.get("breakdown"));
  const donationAmount = calculateTotalAmount(breakdown);
  const processingFee = calculateProcessingFee(donationAmount);

  const ref = searchParams.get("ref");

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"]
  });

  // Initialize Payment Request for Apple Pay / Google Pay
  useEffect(() => {
    if (!stripe || selectedOption !== PAYMENT_METHODS.GOOGLE_APPLE_PAY) {
      setPaymentRequest(null);
      setCanMakePayment(false);
      setCheckingPaymentAvailability(false);
      return;
    }

    setCheckingPaymentAvailability(true);

    const totalWithTip = donationAmount + tipAmount;
    const pr = stripe.paymentRequest({
      country: "CA",
      currency: "cad",
      total: {
        label: "Donation",
        amount: Math.round(totalWithTip * 100) // Convert to cents, includes tip
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: false
    });

    // Check if Payment Request is available
    pr.canMakePayment().then((result) => {
      console.log("Payment Request availability:", result);
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      } else {
        setPaymentRequest(null);
        setCanMakePayment(false);
      }
      setCheckingPaymentAvailability(false);
    }).catch((err) => {
      console.error("Error checking Payment Request availability:", err);
      setPaymentRequest(null);
      setCanMakePayment(false);
      setCheckingPaymentAvailability(false);
    });

    // Handle payment method selection
    pr.on("paymentmethod", async (ev) => {
      try {
        setLoading(true);

        // Extract billing details from Payment Request
        const billing = ev.paymentMethod.billing_details;
        const address = billing.address || {};
        
        // Split name into first and last name
        const nameParts = (billing.name || "").trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        // Build donor details from Payment Request billing details
        const donorDetails = {
          firstName: firstName,
          lastName: lastName,
          email: billing.email || "",
          phone: billing.phone || "",
          addressLine1: address.line1 || "",
          addressLine2: address.line2 || "",
          unit: "",
          city: address.city || "",
          postalCode: address.postal_code || "",
          region: address.state || "",
          country: address.country || "CA",
          businessName: ""
        };

        // Create SetupIntent
        const { clientSecret, customerId } = await createSetupIntent({
          donorDetails,
          paymentMethodType: "card",
          intervalDescription: "Ramadan donation schedule"
        });

        const tipParam = tipAmount;

        // Confirm SetupIntent with Apple/Google Pay payment method
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: ev.paymentMethod.id
        });

        if (error) {
          console.error("Error confirming Apple/Google Pay setup:", error.message);
          alert(t("paymentDetailsSaveCardError") || "Error saving payment method. Please try again.");
          ev.complete("fail");
          setLoading(false);
          return;
        }

        // Success - proceed to next step
        ev.complete("success");
        const params = new URLSearchParams(searchParams);
        params.set("tipAmount", tipParam);
        params.set("paymentMethodId", setupIntent.payment_method);
        params.set("customerId", customerId);
        params.set("stage", "4");
        params.set("selectedOption", "card");
        params.set(
          "formValues",
          encodeURIComponent(JSON.stringify(donorDetails))
        );
        setSearchParams(params);
        setLoading(false);
      } catch (error) {
        console.error("Error processing Apple/Google Pay:", error.message);
        alert(t("paymentDetailsGenericError") || "An error occurred. Please try again.");
        ev.complete("fail");
        setLoading(false);
      }
    });

    // Cleanup
    return () => {
      if (pr) {
        pr.off("paymentmethod");
      }
    };
  }, [
    stripe,
    selectedOption,
    donationAmount,
    formValues,
    tipAmount,
    searchParams,
    setSearchParams,
    t
  ]);

  function validateForm() {
    const errors = {};
    let isValid = true;

    // Fields required
    const requiredFields = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "addressLine1",
      "city",
      "postalCode",
      "selectedRegion",
      "selectedCountry"
    ];

    if (ref && ref.toLocaleLowerCase().includes("ogn")) {
      requiredFields.push("businessName");
    }

    requiredFields.forEach((field) => {
      if (!formValues[field]) {
        isValid = false;
        errors[field] = t("paymentDetailsFieldIsRequired", {
          fieldName: mapFieldToLabel(field, t)
        });
      }
    });

    if (
      formValues.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)
    ) {
      isValid = false;
      errors.email = t("paymentDetailsInvalidEmail");
    }

    if (formValues.unitNumber && isNaN(formValues.unitNumber)) {
      isValid = false;
      errors.unitNumber = t("paymentDetailsUnitMustBeNumber");
    }

    setFormErrors(errors);
    return isValid;
  }

  async function handleNext(finalTipParam) {
    const runId = `pd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    // #region agent log
    fetch("http://127.0.0.1:7554/ingest/209a7807-8ce6-43af-907a-1b8c7625f08b", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "b8fb6e"
      },
      body: JSON.stringify({
        sessionId: "b8fb6e",
        runId,
        hypothesisId: "H1",
        location: "PaymentDetails:index.jsx:handleNext",
        message: "handleNext entered",
        data: {
          selectedOption,
          hasStripe: Boolean(stripe),
          hasElements: Boolean(elements)
        },
        timestamp: Date.now()
      })
    }).catch(() => {});
    // #endregion
    if (!validateForm()) return;

    // For Apple/Google Pay, payment is handled via Payment Request button
    // Just set the tip and return - don't process payment method here
    if (selectedOption === PAYMENT_METHODS.GOOGLE_APPLE_PAY) {
      const tipParam = finalTipParam !== undefined ? finalTipParam : tipAmount;
      if (tipParam !== tipAmount) {
        setTipAmount(tipParam);
      }
      // Payment Request button will handle payment method setup when clicked
      return;
    }

    const donorDetails = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      phone: formValues.phone,
      addressLine1: formValues.addressLine1,
      addressLine2: formValues.addressLine2,
      unit: formValues.unitNumber,
      city: formValues.city,
      postalCode: formValues.postalCode,
      region: formValues.selectedRegion,
      country: formValues.selectedCountry,
      businessName: formValues?.businessName || ""
    };

    try {
      setLoading(true);

      const { clientSecret, customerId } = await createSetupIntent({
        donorDetails,
        paymentMethodType:
          selectedOption === PAYMENT_METHODS.ONLINE_BANKING
            ? "acss_debit"
            : "card",
        intervalDescription: "Ramadan donation schedule"
      });
      // #region agent log
      fetch("http://127.0.0.1:7554/ingest/209a7807-8ce6-43af-907a-1b8c7625f08b", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Debug-Session-Id": "b8fb6e"
        },
        body: JSON.stringify({
          sessionId: "b8fb6e",
          runId,
          hypothesisId: "H2",
          location: "PaymentDetails:index.jsx:handleNext",
          message: "createSetupIntent returned",
          data: {
            customerId: customerId || null,
            hasClientSecret: Boolean(clientSecret),
            selectedOption
          },
          timestamp: Date.now()
        })
      }).catch(() => {});
      // #endregion

      const tipParam = finalTipParam !== undefined ? finalTipParam : tipAmount;

      // ACSS Debit
      if (selectedOption === PAYMENT_METHODS.ONLINE_BANKING) {
        const ipAddress = await getIpAddress();
        const { setupIntent, error } = await stripe.confirmAcssDebitSetup(
          clientSecret,
          {
            payment_method: {
              billing_details: {
                name: `${formValues.firstName} ${formValues.lastName}`,
                email: formValues.email
              }
            },
            mandate_data: {
              customer_acceptance: {
                type: "online",
                online: {
                  ip_address: ipAddress,
                  user_agent: navigator.userAgent
                }
              }
            }
          }
        );

        if (error) {
          console.error("Error confirming ACSS setup:", error.message);
          alert(t("paymentDetailsSaveOnlineBankingError"));
          setLoading(false);
          return;
        }

        const params = new URLSearchParams(searchParams);
        params.set("tipAmount", tipParam);
        params.set("paymentMethodId", setupIntent.payment_method);
        params.set("customerId", customerId);
        params.set("stage", "4");
        params.set("selectedOption", "acss_debit");
        params.set(
          "formValues",
          encodeURIComponent(JSON.stringify(donorDetails))
        );
        setSearchParams(params);
      } else {
        // Credit Card
        const cardElement = elements.getElement(CardElement);
        const { setupIntent, error } = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: t("paymentDetailsNameFormat", {
                  firstName: formValues.firstName,
                  lastName: formValues.lastName
                }),
                email: formValues.email
              }
            }
          }
        );

        if (error) {
          // #region agent log
          fetch("http://127.0.0.1:7554/ingest/209a7807-8ce6-43af-907a-1b8c7625f08b", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Debug-Session-Id": "b8fb6e"
            },
            body: JSON.stringify({
              sessionId: "b8fb6e",
              runId,
              hypothesisId: "H4",
              location: "PaymentDetails:index.jsx:confirmCardSetup",
              message: "confirmCardSetup error",
              data: {
                customerId: customerId || null,
                stripeErrorType: error?.type || null,
                stripeErrorCode: error?.code || null,
                stripeErrorMessage: error?.message || null
              },
              timestamp: Date.now()
            })
          }).catch(() => {});
          // #endregion
          console.error("Error confirming card setup:", error.message);
          alert(t("paymentDetailsSaveCardError"));
          setLoading(false);
          return;
        }

        const params = new URLSearchParams(searchParams);
        params.set("tipAmount", tipParam);
        params.set("paymentMethodId", setupIntent.payment_method);
        params.set("customerId", customerId);
        params.set("stage", "4");
        params.set("selectedOption", "card");
        params.set(
          "formValues",
          encodeURIComponent(JSON.stringify(donorDetails))
        );
        setSearchParams(params);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error creating SetupIntent:", error.message);
      setLoading(false);
      alert(t("paymentDetailsGenericError"));
    }
  }

  return (
    <CustomLayout
      title={t("paymentDetailsTitle")}
      description={t("paymentDetailsDescription")}
      button={
        selectedOption !== PAYMENT_METHODS.GOOGLE_APPLE_PAY ? (
          <CustomButton
            text={t("paymentDetailsButtonNext")}
            onClick={() => {
              if (!validateForm()) return;
              setShowTipModal(true);
            }}
            loading={loading}
          />
        ) : null
      }
    >
      <PaymentOptions
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />

      {(selectedOption === PAYMENT_METHODS.CREDIT_CARD ||
        selectedOption === PAYMENT_METHODS.ONLINE_BANKING) && (
        <CustomerInformation
          isProcessingFeeChecked={isProcessingFeeChecked}
          setIsProcessingFeeChecked={setIsProcessingFeeChecked}
          processingFee={processingFee}
          formValues={formValues}
          setFormValues={setFormValues}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          isLoaded={isLoaded}
          selectedOption={selectedOption}
          refTemplate={ref}
        />
      )}

      {selectedOption === PAYMENT_METHODS.GOOGLE_APPLE_PAY && (
        <>
          {checkingPaymentAvailability && (
            <div style={{ marginTop: "20px", textAlign: "center", padding: "20px" }}>
              <CustomSpinner />
              <p style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
                Checking payment method availability...
              </p>
            </div>
          )}
          
          {!checkingPaymentAvailability && canMakePayment && paymentRequest && (
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <p style={{ marginBottom: "10px", fontSize: "14px", color: "#666" }}>
                {t("paymentDetailsSecureSSL") || "Secure payment with Apple Pay / Google Pay"}
              </p>
              <div id="payment-request-button" ref={paymentRequestButtonRef} style={{ maxWidth: "400px", margin: "0 auto" }}>
                <PaymentRequestButtonElement
                  options={{
                    paymentRequest,
                    style: {
                      paymentRequestButton: {
                        theme: "dark",
                        height: "48px",
                        type: "default"
                      }
                    }
                  }}
                />
              </div>
              <p style={{ marginTop: "10px", fontSize: "12px", color: "#999" }}>
                Click the button above to authenticate with Apple Pay or Google Pay and save your payment method.
              </p>
            </div>
          )}
          
          {!checkingPaymentAvailability && !canMakePayment && (
            <div style={{ marginTop: "20px", padding: "20px", textAlign: "center", backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px", margin: "20px auto", maxWidth: "600px" }}>
              <p style={{ color: "#856404", fontSize: "14px", marginBottom: "10px" }}>
                <strong>Apple Pay / Google Pay is not available on this device or browser.</strong>
              </p>
              <p style={{ color: "#856404", fontSize: "13px" }}>
                Please select "Credit Card" above to continue with your donation.
              </p>
            </div>
          )}
        </>
      )}

      {showTipModal && (
        <TipModal
          donationAmount={donationAmount}
          onClose={() => {
            setShowTipModal(false);
            handleNext(0);
          }}
          onTipSubmit={(tipValue) => {
            const finalTip =
              tipValue < 1 ? donationAmount * tipValue : tipValue;
            setTipAmount(finalTip);
            setShowTipModal(false);
            handleNext(finalTip);
          }}
        />
      )}
    </CustomLayout>
  );
}

function PaymentOptions({ selectedOption, setSelectedOption }) {
  const { t } = useTranslation();

  // Use stable IDs for each option. The label is localized for display.
  const paymentOptions = [
    {
      value: PAYMENT_METHODS.CREDIT_CARD,
      label: t("paymentOptionCreditCard")
    },
    {
      value: PAYMENT_METHODS.GOOGLE_APPLE_PAY,
      label: t("paymentOptionGoogleApplePay")
    }
  ];

  return (
    <motion.div
      className="payment-options-container"
      variants={optionsVariants}
      initial="hidden"
      animate="visible"
    >
      {paymentOptions.map((opt) => (
        <CustomCheckbox
          key={opt.value}
          option={opt.label}
          isSelected={opt.value === selectedOption}
          onSelect={() => setSelectedOption(opt.value)}
        />
      ))}
    </motion.div>
  );
}

function CustomerInformation({
  isProcessingFeeChecked,
  setIsProcessingFeeChecked,
  processingFee,
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
  isLoaded,
  selectedOption,
  refTemplate
}) {
  const { t } = useTranslation();

  const cardElementOptions = {
    hidePostalCode: true,
    style: {
      base: {
        color: "#32325d",
        fontSize: "16px",
        fontSmoothing: "antialiased",
        backgroundColor: "#fff",
        "::placeholder": {
          color: "#aab7c4"
        }
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a"
      }
    }
  };

  return (
    <motion.div
      className="credit-card-info"
      variants={detailsFormVariants}
      initial="hidden"
      animate="visible"
    >
      {selectedOption === PAYMENT_METHODS.CREDIT_CARD && (
        <>
          <p className="text-info">
            <MdLock className="lock-icon" /> {t("paymentDetailsSecureSSL")}
          </p>
          <div className="card-element-wrapper">
            <CardElement options={cardElementOptions} />
          </div>
        </>
      )}

      <Details
        formValues={formValues}
        setFormValues={setFormValues}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        isLoaded={isLoaded}
        refTemplate={refTemplate}
      />

      {/* If you want to re-enable the processing fee checkbox:
         <ProcessingFeeCheckbox
           isChecked={isProcessingFeeChecked}
           onCheck={() => setIsProcessingFeeChecked(!isProcessingFeeChecked)}
           feeAmount={processingFee}
         />
      */}
    </motion.div>
  );
}

// Tools to map each field to a user-friendly label (localized).
function mapFieldToLabel(field, t) {
  switch (field) {
    case "firstName":
      return t("paymentDetailsFirstName");
    case "middleName":
      return t("paymentDetailsMiddleName");
    case "lastName":
      return t("paymentDetailsLastName");
    case "businessName":
      return t("paymentDetailsBusinessName");
    case "email":
      return t("paymentDetailsEmail");
    case "phone":
      return t("paymentDetailsPhone");
    case "addressLine1":
      return t("paymentDetailsAddressLine1");
    case "addressLine2":
      return t("paymentDetailsAddressLine2");
    case "unitNumber":
      return t("paymentDetailsUnitNumber");
    case "city":
      return t("paymentDetailsCity");
    case "postalCode":
      return t("paymentDetailsPostalCode");
    case "selectedRegion":
      return t("paymentDetailsSelectRegion");
    case "selectedCountry":
      return t("paymentDetailsSelectCountry");
    default:
      return field;
  }
}

// The detail form for personal & address fields
function Details({
  formValues,
  setFormValues,
  formErrors,
  setFormErrors,
  isLoaded,
  refTemplate
}) {
  const { t } = useTranslation();
  const addressInputRef = useRef(null);

  const [emailLoading, setEmailLoading] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState(null);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState(null);

  useEffect(() => {
    if (isLoaded && addressInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        {
          types: ["address"],
          componentRestrictions: { country: ["ca"] }
        }
      );

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const addressComponents = place.address_components;
        let streetNumber = "";
        let route = "";
        let postal = "";
        let city = "";
        let unit = "";
        let region = "";
        let country = "CA";

        addressComponents.forEach((component) => {
          const types = component.types;
          if (types.includes("street_number"))
            streetNumber = component.long_name;
          if (types.includes("route")) route = component.long_name;
          if (types.includes("postal_code")) postal = component.long_name;
          if (types.includes("locality")) city = component.long_name;
          if (types.includes("subpremise")) unit = component.long_name;
          if (types.includes("administrative_area_level_1"))
            region = component.long_name;
          if (types.includes("country")) country = component.short_name;
        });

        setFormValues((prev) => ({
          ...prev,
          addressLine1: `${streetNumber} ${route}`.trim(),
          postalCode: postal,
          city,
          unitNumber: unit,
          selectedRegion: region,
          selectedCountry: country || "CA"
        }));
      });
    }
  }, [isLoaded, setFormValues]);

  function handleInputChange(field) {
    return (e) => {
      const { value } = e.target;
      setFormValues((prev) => ({ ...prev, [field]: value }));
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    };
  }

  function handlePhoneChange(e) {
    const numeric = e.target.value.replace(/\D/g, "");
    setFormValues((prev) => ({ ...prev, phone: numeric }));
    setFormErrors((prev) => ({ ...prev, phone: "" }));
  }

  function handleUnitNumberChange(e) {
    const numeric = e.target.value.replace(/\D/g, "");
    setFormValues((prev) => ({ ...prev, unitNumber: numeric }));
  }

  async function handleEmailBlur() {
    setEmailLoading(true);
    try {
      const resp = await verifyEmail(formValues.email);
      setEmailErrorMessage(resp.isValid ? null : resp.message);
    } catch (err) {
      console.error("Error validating email:", err.message);
    } finally {
      setEmailLoading(false);
    }
  }

  async function handlePhoneBlur() {
    setPhoneLoading(true);
    try {
      const resp = await verifyPhone(
        formValues.phone,
        formValues.selectedCountry
      );
      setPhoneErrorMessage(resp.isValid ? null : resp.message);
    } catch (err) {
      console.error("Error validating phone:", err.message);
    } finally {
      setPhoneLoading(false);
    }
  }

  return (
    <div className="details-container">
      <p className="text-info">{t("paymentDetailsYourDetails")}</p>

      <form>
        {refTemplate && refTemplate.toLowerCase().includes("ogn") && (
          <>
            <div className="details-form-row">
              <InputField
                id="businessName"
                name="businessName"
                placeholder={`${t("paymentDetailsBusinessName")} *`}
                value={formValues.businessName}
                onChange={handleInputChange("businessName")}
                required
                icon={<MdPerson />}
                maxLength="19"
                error={formErrors.businessName}
              />
            </div>
          </>
        )}

        <div className="details-form-row">
          <InputField
            id="firstName"
            name="firstName"
            placeholder={`${t("paymentDetailsFirstName")} *`}
            value={formValues.firstName}
            onChange={handleInputChange("firstName")}
            required
            icon={<MdAddBusiness />}
            error={formErrors.firstName}
            maxLength="19"
          />
          <InputField
            id="middleName"
            name="middleName"
            placeholder={t("paymentDetailsMiddleName")}
            value={formValues.middleName}
            onChange={handleInputChange("middleName")}
            icon={<MdPerson />}
          />
          <InputField
            id="lastName"
            name="lastName"
            placeholder={`${t("paymentDetailsLastName")} *`}
            value={formValues.lastName}
            onChange={handleInputChange("lastName")}
            required
            icon={<MdPerson />}
            error={formErrors.lastName}
          />
        </div>

        <div className="details-form-row">
          <InputField
            type="email"
            id="email"
            name="email"
            placeholder={`${t("paymentDetailsEmail")} *`}
            value={formValues.email}
            onChange={handleInputChange("email")}
            // onBlur={handleEmailBlur}
            required
            icon={<MdEmail />}
            error={formErrors.email}
            loading={emailLoading}
            additionalErrorMessage={emailErrorMessage}
          />

          <InputField
            id="phone"
            name="phone"
            placeholder={`${t("paymentDetailsPhone")} *`}
            value={formValues.phone}
            onChange={handlePhoneChange}
            // onBlur={handlePhoneBlur}
            required
            icon={<MdPhone />}
            error={formErrors.phone}
            loading={phoneLoading}
            additionalErrorMessage={phoneErrorMessage}
          />
        </div>

        <div className="details-form-row">
          <InputField
            id="addressLine1"
            name="addressLine1"
            placeholder={`${t("paymentDetailsAddressLine1")} *`}
            value={formValues.addressLine1}
            onChange={handleInputChange("addressLine1")}
            required
            icon={<MdLocationOn />}
            error={formErrors.addressLine1}
            inputRef={addressInputRef}
          />
        </div>

        <div className="details-form-row">
          <InputField
            id="addressLine2"
            name="addressLine2"
            placeholder={t("paymentDetailsAddressLine2")}
            value={formValues.addressLine2}
            onChange={handleInputChange("addressLine2")}
            icon={<MdLocationOn />}
          />
        </div>

        <div className="details-form-row">
          <InputField
            id="unit"
            name="unit"
            placeholder={t("paymentDetailsUnitNumber")}
            value={formValues.unitNumber}
            onChange={handleUnitNumberChange}
            icon={<MdDoorBack />}
            error={formErrors.unitNumber}
          />
          <InputField
            id="city"
            name="city"
            placeholder={`${t("paymentDetailsCity")} *`}
            value={formValues.city}
            onChange={handleInputChange("city")}
            required
            icon={<MdLocationCity />}
            error={formErrors.city}
          />
          <InputField
            id="postal"
            name="postal"
            placeholder={`${t("paymentDetailsPostalCode")} *`}
            value={formValues.postalCode}
            onChange={handleInputChange("postalCode")}
            required
            icon={<MdMarkunreadMailbox />}
            error={formErrors.postalCode}
          />
        </div>

        <div className="details-form-row">
          <InputSelect
            id="selectedRegion"
            name="selectedRegion"
            placeholder={`${t("paymentDetailsSelectRegion")} *`}
            value={formValues.selectedRegion}
            onChange={(val) =>
              handleInputChange("selectedRegion")({ target: { value: val } })
            }
            required
            icon={<MdMap />}
            error={formErrors.selectedRegion}
            optionsComponent={
              <RegionDropdown
                country={formValues.selectedCountry}
                value={formValues.selectedRegion}
                onChange={(val) =>
                  handleInputChange("selectedRegion")({
                    target: { value: val }
                  })
                }
                countryValueType="short"
                classes="region-dropdown"
              />
            }
          />

          <InputSelect
            id="selectedCountry"
            name="selectedCountry"
            placeholder={`${t("paymentDetailsSelectCountry")} *`}
            value={formValues.selectedCountry}
            onChange={(val) =>
              handleInputChange("selectedCountry")({ target: { value: val } })
            }
            required
            icon={<MdPublic />}
            error={formErrors.selectedCountry}
            optionsComponent={
              <CountryDropdown
                value={formValues.selectedCountry}
                onChange={(val) =>
                  handleInputChange("selectedCountry")({
                    target: { value: val }
                  })
                }
                valueType="short"
                classes="country-dropdown"
              />
            }
          />
        </div>
      </form>
    </div>
  );
}

function InputField({
  type = "text",
  id,
  name,
  placeholder,
  value,
  onChange,
  onBlur,
  required = false,
  maxLength,
  icon,
  error,
  inputRef,
  loading,
  additionalErrorMessage
}) {
  return (
    <div className="input-with-icon-outer">
      <div className="input-with-icon">
        {icon && React.cloneElement(icon, { className: "input-icon" })}
        <input
          type={type}
          id={id}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          maxLength={maxLength}
          ref={inputRef}
        />
      </div>
      {error && <span className="error-text">{error}</span>}
      {loading ? (
        <CustomSpinner />
      ) : (
        additionalErrorMessage && (
          <span className="error-text">{additionalErrorMessage}</span>
        )
      )}
    </div>
  );
}

function InputSelect({ icon, error, optionsComponent }) {
  return (
    <div className="input-with-icon-outer">
      <div className="input-with-icon">
        {icon && React.cloneElement(icon, { className: "input-icon" })}
        {optionsComponent}
      </div>
      {error && <span className="error-text">{error}</span>}
    </div>
  );
}

function ProcessingFeeCheckbox({ isChecked, onCheck, feeAmount }) {
  // If you'd like this text localized, add keys in your translation file
  return (
    <div
      className={`processing-fee-checkbox ${isChecked ? "checked" : ""}`}
      onClick={onCheck}
    >
      <div className="checkbox-container">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={onCheck}
          className="hidden-checkbox"
        />
        <div className="custom-checkbox">
          <span className="checkmark">&#10003;</span>
        </div>
      </div>
      <div className="processing-fee-content">
        <p className="processing-fee-title">
          I want to cover the processing fees
        </p>
        <p className="processing-fee-description">
          Covering the fees will add{" "}
          <strong>${parseFloat(feeAmount).toFixed(2)}</strong> (2.2% + $0.80) to
          the donation total.
        </p>
      </div>
    </div>
  );
}
